import { useNavigate } from "react-router-dom";
import { useAuth, useTheme } from "../App";
import { Button } from "./ui/button";
import {
  Sparkles,
  BookOpen,
  History,
  Settings,
  Sun,
  Moon,
  ChevronDown,
  TrendingUp,
} from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "./ui/dropdown-menu";

const TOPIC_LABELS = {
  "ai-ml": "AI & ML",
  "cloud": "Cloud",
  "devops": "DevOps",
  "security": "Security",
  "web-dev": "Web Dev",
  "mobile": "Mobile",
  "data-science": "Data Science",
  "blockchain": "Blockchain",
  "engineering": "Engineering",
  "product": "Product",
  "ux-design": "UX Design",
  "startup": "Startup",
  "leadership": "Leadership",
  "career": "Career",
  "open-source": "Open Source",
  "programming": "Programming",
};

/**
 * AppShell — shared header + left sidebar layout.
 *
 * Props:
 *   activeNav       "feed" | "shortlist" | "history" | "settings"
 *   userTopics      string[]  topic ids shown under Feed (FeedPage only)
 *   selectedTopic   string | null  currently active topic filter
 *   onTopicFilter   (topic) => void  called when sidebar topic is clicked
 *   headerRight     ReactNode  extra content in the header right slot
 *   children        ReactNode  page content rendered in <main>
 */
const AppShell = ({
  children,
  activeNav = "feed",
  userTopics = [],
  selectedTopic = null,
  onTopicFilter = null,
  headerRight = null,
}) => {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const { theme, toggleTheme } = useTheme();

  const handleLogout = async () => {
    await logout();
  };

  /* ── Sidebar helpers ── */
  const NavItem = ({ label, icon, onClick, testId, isActive }) => (
    <button
      onClick={onClick}
      data-testid={testId}
      className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-md text-sm font-medium transition-all duration-150 ${
        isActive
          ? "bg-primary/10 text-primary"
          : "text-muted-foreground hover:bg-card hover:text-foreground"
      }`}
    >
      {icon}
      {label}
    </button>
  );

  const TopicItem = ({ label, value, testId }) => {
    const active = selectedTopic === value;
    return (
      <button
        onClick={() => onTopicFilter?.(value)}
        data-testid={testId}
        className={`w-full flex items-center justify-between px-3 py-2 rounded-md text-sm font-medium transition-all duration-150 ${
          active
            ? "bg-primary/10 text-primary"
            : "text-muted-foreground hover:bg-card hover:text-foreground"
        }`}
      >
        <span>{label}</span>
        {active && <span className="w-1.5 h-1.5 rounded-full bg-primary shrink-0" />}
      </button>
    );
  };

  return (
    <div className="min-h-screen bg-background flex flex-col">
      {/* ── Header ── */}
      <header className="border-b border-border glass-panel bg-background/80 sticky top-0 z-50 shrink-0">
        <div className="px-4 sm:px-6">
          <div className="flex items-center justify-between h-16">
            {/* Brand */}
            <div className="flex items-center gap-3">
              <img src="/logo.png" alt="PostWriter" className="w-8 h-8" />
              <div>
                <span className="font-headline font-bold text-lg tracking-tight text-primary">
                  PostWriter
                </span>
              </div>
            </div>

            {/* Right slot */}
            <div className="flex items-center gap-2">
              {headerRight}

              <Button
                variant="ghost"
                size="icon"
                onClick={toggleTheme}
                data-testid="theme-toggle"
              >
                {theme === "dark" ? (
                  <Sun className="w-5 h-5" />
                ) : (
                  <Moon className="w-5 h-5" />
                )}
              </Button>

              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="ghost"
                    className="flex items-center gap-2 px-2"
                    data-testid="user-menu"
                  >
                    {user?.picture ? (
                      <img
                        src={user.picture}
                        alt={user.name}
                        className="w-8 h-8 rounded-full"
                      />
                    ) : (
                      <div className="w-8 h-8 bg-primary rounded-full flex items-center justify-center text-primary-foreground font-medium">
                        {user?.name?.charAt(0) || "U"}
                      </div>
                    )}
                    <ChevronDown className="w-4 h-4 text-muted-foreground" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                  <div className="px-2 py-1.5">
                    <p className="font-medium text-sm">{user?.name}</p>
                    <p className="text-xs text-muted-foreground">{user?.email}</p>
                  </div>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem
                    onClick={() => navigate("/shortlist")}
                    data-testid="shortlist-link"
                    className="font-medium text-primary"
                  >
                    <Sparkles className="w-4 h-4 mr-2" />
                    Repurposing Vault
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem
                    onClick={() => navigate("/history")}
                    data-testid="history-link"
                  >
                    <History className="w-4 h-4 mr-2" />
                    Post History
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    onClick={() => navigate("/settings")}
                    data-testid="settings-link"
                  >
                    <Settings className="w-4 h-4 mr-2" />
                    Settings
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem
                    onClick={handleLogout}
                    data-testid="logout-btn"
                    className="text-destructive"
                  >
                    Log out
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        </div>
      </header>

      {/* ── Body ── */}
      <div className="flex flex-1 min-h-0">
        {/* Left Sidebar — desktop only */}
        <aside className="hidden lg:flex flex-col w-56 xl:w-60 bg-secondary border-r border-border shrink-0 sticky top-16 h-[calc(100vh-4rem)] overflow-y-auto">
          <div className="p-4 flex flex-col gap-0.5">
            <p className="text-[10px] font-label font-semibold uppercase tracking-[0.18em] text-muted-foreground px-3 mb-1 mt-2">
              Navigate
            </p>

            {/* Feed */}
            <NavItem
              label="Feed"
              icon={<Sparkles className="w-4 h-4" />}
              onClick={() => navigate("/feed")}
              testId="sidebar-feed"
              isActive={activeNav === "feed"}
            />

            {/* Topics nested under Feed — always visible */}
            {userTopics.length > 0 && (
              <div className="pl-4 flex flex-col gap-0.5 mt-0.5">
                <TopicItem label="All Topics" value={null} testId="filter-all" />
                {userTopics.map((topic) => (
                  <TopicItem
                    key={topic}
                    label={TOPIC_LABELS[topic] || topic}
                    value={topic}
                    testId={`filter-${topic}`}
                  />
                ))}
                <TopicItem label="Others" value="__others" testId="filter-others" />
                <button
                  onClick={() => onTopicFilter?.("__trending")}
                  data-testid="filter-trending"
                  className={`w-full flex items-center justify-between px-3 py-2 rounded-md text-sm font-medium transition-all duration-150 ${
                    selectedTopic === "__trending"
                      ? "bg-orange-500/10 text-orange-500"
                      : "text-muted-foreground hover:bg-card hover:text-foreground"
                  }`}
                >
                  <span className="flex items-center gap-2">
                    <TrendingUp className="w-3.5 h-3.5" />
                    Trending
                  </span>
                  {selectedTopic === "__trending" && (
                    <span className="w-1.5 h-1.5 rounded-full bg-orange-500 shrink-0" />
                  )}
                </button>
              </div>
            )}

            <div className="border-t border-border my-3" />

            <NavItem
              label="Repurposing Vault"
              icon={<BookOpen className="w-4 h-4" />}
              onClick={() => navigate("/shortlist")}
              testId="sidebar-shortlist"
              isActive={activeNav === "shortlist"}
            />
            <NavItem
              label="Post History"
              icon={<History className="w-4 h-4" />}
              onClick={() => navigate("/history")}
              testId="sidebar-history"
              isActive={activeNav === "history"}
            />
            <NavItem
              label="Settings"
              icon={<Settings className="w-4 h-4" />}
              onClick={() => navigate("/settings")}
              testId="sidebar-settings"
              isActive={activeNav === "settings"}
            />
          </div>
        </aside>

        {/* Page content */}
        <main className="flex-1 overflow-y-auto">
          {children}
        </main>
      </div>
    </div>
  );
};

export default AppShell;
